import React from 'react';
import { getStatusColor, type BasicStatus } from '../../types/predictions';

interface ThresholdBadgeProps {
  status: BasicStatus;
  pointsFromThreshold: number;
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
}

/**
 * Status Badge Component
 * Displays color-coded status badge with points from threshold
 *
 * Colors:
 * - EXCELLENT (< 50% of threshold): Green
 * - SAFE (50-90% of threshold): Blue
 * - WARNING (90-100% of threshold): Yellow
 * - CRITICAL (> threshold): Red
 */
export const ThresholdBadge: React.FC<ThresholdBadgeProps> = ({
  status,
  pointsFromThreshold,
  size = 'medium',
  showTooltip = true,
}) => {
  const colors = getStatusColor(status);

  // Size configurations
  const sizeClasses = {
    small: 'px-2 py-1 text-xs',
    medium: 'px-3 py-1.5 text-sm',
    large: 'px-4 py-2 text-base',
  };

  // Tooltip messages - simplified
  const getTooltipMessage = (): string => {
    const delta = pointsFromThreshold > 0 ? `-${pointsFromThreshold}` : `+${Math.abs(pointsFromThreshold)}`;
    return `${status} (${delta} from threshold)`;
  };

  return (
    <div className="relative inline-block group">
      <span
        className={`
          inline-flex items-center gap-2 rounded-full font-semibold
          ${sizeClasses[size]}
        `}
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `2px solid ${colors.border}`,
        }}
      >
        {/* Status indicator dot */}
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: colors.border }}
        />

        {/* Status text */}
        <span>{status}</span>

        {/* Points from threshold */}
        {pointsFromThreshold !== 0 && (
          <span className="text-xs opacity-75">
            ({pointsFromThreshold > 0 ? '-' : '+'}{Math.abs(pointsFromThreshold)})
          </span>
        )}
      </span>

      {/* Tooltip */}
      {showTooltip && (
        <div
          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2
                     bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0
                     group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
                     whitespace-nowrap z-10"
        >
          {getTooltipMessage()}
          {/* Arrow */}
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2
                       border-4 border-transparent border-t-gray-900"
          />
        </div>
      )}
    </div>
  );
};

/**
 * Compact version of ThresholdBadge for use in tables or lists
 */
export const ThresholdBadgeCompact: React.FC<{
  status: BasicStatus;
  percentile: number;
  threshold: number;
}> = ({ status, percentile, threshold }) => {
  const colors = getStatusColor(status);
  const pointsFromThreshold = threshold - percentile;

  return (
    <div className="inline-flex items-center gap-1">
      <span
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: colors.border }}
        title={`${status}: ${percentile}/${threshold}`}
      />
      <span className="text-xs font-medium" style={{ color: colors.text }}>
        {percentile}
      </span>
      <span className="text-xs text-gray-400">/</span>
      <span className="text-xs text-gray-600">{threshold}</span>
      {pointsFromThreshold > 0 && (
        <span className="text-xs text-gray-500">
          (-{pointsFromThreshold})
        </span>
      )}
    </div>
  );
};

export default ThresholdBadge;
